import { useEffect, useMemo, useState } from 'react';
import { api, type CustTrendGroup, type CustTrendRow } from '../lib/api';
import { useSticky } from '../lib/sticky';
import { canScreen, useMe } from '../lib/me';
import Modal from '../components/Modal';
import { ErrorBox, money } from '../components/DataTable';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 거래처 추세(10-27 way — 「거래처별 추세를 전 직원이 언제든」). 이 탭만 전 직원이 본다.
 * 대체: 구글시트 `Sales Report`·`ReportView` + 데이터 스튜디오 `SalesReportViewer`(담당자별 시트 사본 · 거래처 고르면 달별 수입·수익·%·수익증감).
 * 서버 `functions/src/profitCustTrend.ts` — 매일 05:30 다시 센 보관 표를 읽는다(값 = 거래처별 탭과 같은 계산).
 */

type Kind = '전체' | '통근' | '일반';
type Span = 12 | 24 | 36 | 0;
interface MonAgg { mon: string; rev: number; exp: number; dalloc: number; profit: number; manager: string; n: number }

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const pct = (v: number | null) => (v === null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`);
const signed = (v: number) => `${v >= 0 ? '+' : ''}${money(v)}`;
const eok = (v: number) => (Math.abs(v) >= 1e8 ? `${(v / 1e8).toFixed(1)}억` : `${Math.round(v / 1e4).toLocaleString('ko-KR')}만`);
const prevMonOf = (m: string) => { const [y, mm] = m.split('-').map(Number) as [number, number]; return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, '0')}`; };
function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to; ) {
    out.push(m);
    const [y, mm] = m.split('-').map(Number) as [number, number];
    m = mm === 12 ? `${y + 1}-01` : `${y}-${String(mm + 1).padStart(2, '0')}`;
  }
  return out;
}

export default function ProfitCustTrend() {
  const { me } = useMe();
  const [list, setList] = useState<{ months: string[]; cur: string; computedAt: string | null; groups: CustTrendGroup[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [mine, setMine] = useSticky<boolean>('profit.ctrend.mine', false);
  const [showOld, setShowOld] = useState(false);
  const [cust, setCust] = useSticky<string>('profit.ctrend.cust', '');
  const [reload, setReload] = useState(0);
  const canGroup = canScreen(me, 'profit', 'write');

  useEffect(() => {
    api.listProfitCustTrend({}).then(setList).catch((e) => setError(errMsg(e)));
  }, [reload]);

  const myName = me?.name?.trim() ?? '';
  const hasMine = useMemo(() => !!myName && !!list?.groups.some((g) => g.managers.includes(myName)), [list, myName]);
  const shown = useMemo(() => {
    if (!list) return [];
    const word = q.trim();
    return list.groups
      .filter((g) => (showOld || word || Number(g.rev12) !== 0))
      .filter((g) => !mine || !hasMine || g.managers.includes(myName))
      .filter((g) => !word || g.cust.includes(word) || g.managers.some((m) => m.includes(word)))
      .sort((a, b) => Number(b.rev12) - Number(a.rev12) || b.last_mon.localeCompare(a.last_mon));
  }, [list, q, mine, hasMine, myName, showOld]);
  // 고른 거래처가 없으면(처음) 목록 맨 위
  const current = cust && list?.groups.some((g) => g.cust === cust) ? cust : shown[0]?.cust ?? '';

  if (error) return <ErrorBox message={error} />;
  if (!list) return <div className="py-10 text-center text-sm text-ink-soft">불러오는 중…</div>;
  const old = list.groups.filter((g) => Number(g.rev12) === 0).length;

  return (
    <div className="flex flex-col gap-3 lg:flex-row">
      <section className="card flex flex-col lg:w-[24rem] lg:shrink-0">
        <div className="space-y-2 border-b border-line p-2">
          <input className="field w-full" placeholder="거래처·버스인접수담당자 찾기" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="flex flex-wrap items-center gap-1 text-sm">
            <button className={!mine || !hasMine ? 'btn-primary' : 'btn'} onClick={() => setMine(false)}>전체</button>
            {hasMine && <button className={mine ? 'btn-primary' : 'btn'} onClick={() => setMine(true)}>내 담당</button>}
            <label className="ml-1 flex items-center gap-1 text-xs text-ink-soft">
              <input type="checkbox" checked={showOld} onChange={(e) => setShowOld(e.target.checked)} /> 최근 1년 거래 없음 {old}곳
            </label>
          </div>
          <div className="text-xs text-ink-faint">
            {shown.length}곳 · 최근 12개월({list.months[0]}~{list.months.at(-1)}) 매출순
          </div>
        </div>
        <ul className="max-h-[40vh] overflow-auto lg:max-h-[calc(100vh-14rem)]">
          {shown.map((g) => {
            const rev = Number(g.rev12), profit = Number(g.profit12);
            return (
              <li key={g.cust}>
                <button className={`flex w-full items-center gap-2 border-b border-line/60 px-2 py-1.5 text-left hover:bg-surface-soft ${g.cust === current ? 'bg-brand-50' : ''}`}
                        onClick={() => setCust(g.cust)}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm break-keep">{g.cust}{g.members > 1 && <span className="ml-1 text-xs text-ink-faint">({g.members}곳)</span>}</span>
                    <span className="block truncate text-xs text-ink-soft">{g.managers.join(', ') || '—'} · {g.types.join('·')}</span>
                  </span>
                  <Spark values={g.spark} />
                  <span className="w-20 shrink-0 text-right text-xs tabular-nums">
                    {rev ? eok(rev) : <span className="text-ink-faint">{g.last_mon}</span>}
                    {rev ? <span className={`block ${profit < 0 ? 'text-red-700' : 'text-ink-soft'}`}>{pct(profit / rev)}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
          {!shown.length && <li className="p-4 text-center text-xs text-ink-faint">맞는 거래처가 없습니다.</li>}
        </ul>
      </section>
      <div className="min-w-0 flex-1">
        {current ? <Detail key={`${current}:${reload}`} cust={current} cur={list.cur} computedAt={list.computedAt}
                            onGroup={canGroup ? (g) => { if (g !== current) setCust(g); setReload((n) => n + 1); } : undefined} /> : <div className="card p-6 text-sm text-ink-soft">거래처를 골라 주세요.</div>}
      </div>
    </div>
  );
}

/** 목록 줄의 작은 선 — 최근 12개월 수익. 없는 달은 끊는다. */
function Spark({ values }: { values: (string | null)[] }) {
  const vs = values.map((v) => (v === null ? null : Number(v)));
  const nums = vs.filter((v): v is number => v !== null);
  if (nums.length < 2) return <span className="w-16 shrink-0" />;
  const lo = Math.min(0, ...nums), hi = Math.max(0, ...nums), W = 64, H = 20;
  const x = (i: number) => (i * W) / (vs.length - 1);
  const y = (v: number) => H - 1 - ((v - lo) / (hi - lo || 1)) * (H - 2);
  const segs: string[][] = []; let cur: string[] = [];
  vs.forEach((v, i) => { if (v === null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(`${x(i)},${y(v)}`); });
  if (cur.length) segs.push(cur);
  return (
    <svg width={W} height={H} className="shrink-0" aria-hidden>
      {lo < 0 && <line x1={0} x2={W} y1={y(0)} y2={y(0)} className="stroke-line" strokeWidth={1} />}
      {segs.map((s, k) => <polyline key={k} fill="none" className="stroke-brand-600" strokeWidth={1.5} points={s.join(' ')} />)}
    </svg>
  );
}

function Detail({ cust, cur, computedAt, onGroup }: { cust: string; cur: string; computedAt: string | null; onGroup?: (group: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<CustTrendRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<Kind>('전체');
  const [span, setSpan] = useSticky<Span>('profit.ctrend.span', 24);

  useEffect(() => {
    let alive = true;
    api.getProfitCustTrend({ cust }).then((r) => { if (alive) setRows(r.rows); }).catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
  }, [cust]);

  const types = useMemo(() => [...new Set((rows ?? []).map((r) => r.recv_type))].sort(), [rows]);
  const members = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) m.set(r.raw_name, (m.get(r.raw_name) ?? 0) + Number(r.rev));
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [rows]);
  const byMon = useMemo(() => {
    const m = new Map<string, MonAgg & { top: number }>();
    for (const r of rows ?? []) {
      if (kind !== '전체' && r.recv_type !== kind) continue;
      const rev = Number(r.rev), exp = Number(r.exp), dalloc = Number(r.dalloc);
      const o = m.get(r.mon) ?? { mon: r.mon, rev: 0, exp: 0, dalloc: 0, profit: 0, manager: '', n: 0, top: -Infinity };
      o.rev += rev; o.exp += exp; o.dalloc += dalloc; o.profit += rev - exp - dalloc; o.n += r.n;
      if (r.manager && rev > o.top) { o.top = rev; o.manager = r.manager; }
      m.set(r.mon, o);
    }
    return m;
  }, [rows, kind]);

  if (error) return <ErrorBox message={error} />;
  if (!rows) return <div className="card py-10 text-center text-sm text-ink-soft">불러오는 중…</div>;
  const all = [...byMon.keys()].sort();
  if (!all.length) return <div className="card p-6 text-sm text-ink-soft">{cust} — 이 구분의 기록이 없습니다.</div>;
  const last = all.at(-1)!;
  const axis = monthsBetween(span ? (monthsBetween(all[0]!, last).slice(-span)[0] ?? all[0]!) : all[0]!, last);
  const inSpan = axis.filter((m) => byMon.has(m)).map((m) => byMon.get(m)!);
  const done = inSpan.filter((r) => r.mon !== cur);
  const sum = done.reduce((t, r) => ({ rev: t.rev + r.rev, profit: t.profit + r.profit }), { rev: 0, profit: 0 });
  const best = done.length ? done.reduce((a, b) => (b.profit > a.profit ? b : a)) : null;
  const worst = done.length ? done.reduce((a, b) => (b.profit < a.profit ? b : a)) : null;
  const lastDone = done.at(-1);
  const yearAgo = lastDone ? byMon.get(`${Number(lastDone.mon.slice(0, 4)) - 1}${lastDone.mon.slice(4)}`) : undefined;

  return (
    <div className="space-y-3">
      <section className="card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold break-keep">{cust}</h2>
          {members.length > 1 && <span className="text-xs text-ink-soft" title={members.join('\n')}>묶음 {members.length}곳</span>}
          {onGroup && <button className="btn px-2 py-0.5 text-xs" onClick={() => setEditing(true)}>묶음 고치기</button>}
          <div className="ml-auto flex flex-wrap items-center gap-1 text-sm">
            {types.length > 1 && (['전체', ...types] as Kind[]).map((k) => <button key={k} className={kind === k ? 'btn-primary' : 'btn'} onClick={() => setKind(k)}>{k}</button>)}
            <select className="field w-auto" value={span} onChange={(e) => setSpan(Number(e.target.value) as Span)} aria-label="기간">
              {([12, 24, 36, 0] as const).map((s) => <option key={s} value={s}>{s ? `최근 ${s}개월` : `전체(${all[0]}~)`}</option>)}
            </select>
            <CalcNote>
              <div>매출 = 청구액 · 지출 = 운행료·영업정산 지출계 + 선지급 공제 되돌림 + (통근) 직영차량 비용 배분 · 수익 = 매출 − 지출. 거래처별 탭과 같은 계산입니다.</div>
              <div>청구상태(청구 요청·완료)가 있는 접수만 셉니다. 이번 달({cur})은 진행 중이라 합계·최고/최저에서 뺍니다.</div>
              <div>계열사·캠퍼스는 거래처 묶음표대로 한 줄로 봅니다{members.length > 1 ? ` — ${members.join(', ')}` : ''}.</div>
              <div>매일 새벽 5시 30분에 다시 셉니다{computedAt ? ` (마지막 ${computedAt.slice(0, 16)})` : ''} — 오늘 고친 정산은 내일 반영됩니다.</div>
            </CalcNote>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Stat label={`기간 매출 (${done.length}개월)`} value={eok(sum.rev)} />
          <Stat label="기간 수익 · 수익률" value={`${eok(sum.profit)} · ${pct(sum.rev ? sum.profit / sum.rev : null)}`} tone={sum.profit < 0} />
          <Stat label={lastDone ? `${lastDone.mon} 수익 (작년 같은 달 대비)` : '최근 달'} value={lastDone ? eok(lastDone.profit) : '—'}
                sub={lastDone && yearAgo ? signed(lastDone.profit - yearAgo.profit) : undefined} subTone={lastDone && yearAgo ? lastDone.profit - yearAgo.profit < 0 : false} />
          <Stat label="수익 최고 · 최저 달" value={best && worst ? `${best.mon.slice(2)} · ${worst.mon.slice(2)}` : '—'}
                sub={best && worst ? `${eok(best.profit)} · ${eok(worst.profit)}` : undefined} />
        </div>
      </section>

      {editing && onGroup && <GroupEditor cust={cust} members={members} onClose={() => setEditing(false)} onSaved={(g) => { setEditing(false); onGroup(g); }} />}

      <section className="card p-3">
        <TrendChart axis={axis} byMon={byMon} cur={cur} />
      </section>

      <section className="card max-h-[calc(100vh-14rem)] overflow-auto">
        <table className="w-full min-w-[44rem] text-sm tabular-nums">
          <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
            <tr className="border-b border-line">
              <th className="px-2 py-1.5 text-left">정산월</th>
              <th className="px-2 py-1.5 text-right">매출</th>
              <th className="px-2 py-1.5 text-right">지출</th>
              <th className="px-2 py-1.5 text-right">수익</th>
              <th className="px-2 py-1.5 text-right">수익률</th>
              <th className="px-2 py-1.5 text-right">수익 증감(전월)</th>
              <th className="px-2 py-1.5 text-left">버스인접수담당자</th>
            </tr>
          </thead>
          <tbody>
            {[...inSpan].reverse().map((r) => {
              const p = byMon.get(prevMonOf(r.mon));
              const d = p ? r.profit - p.profit : null;
              return (
                <tr key={r.mon} className={`border-b border-line/60 ${r.mon === cur ? 'text-ink-soft' : ''}`}>
                  <td className="px-2 py-1 whitespace-nowrap">{r.mon}{r.mon === cur && <span className="ml-1 rounded bg-amber-50 px-1 text-xs text-amber-700">진행 중</span>}</td>
                  <td className="px-2 py-1 text-right">{money(Math.round(r.rev))}</td>
                  <td className="px-2 py-1 text-right" title={r.dalloc ? `직영차량 배분 ${money(Math.round(r.dalloc))} 포함` : undefined}>{money(Math.round(r.exp + r.dalloc))}</td>
                  <td className={`px-2 py-1 text-right ${r.profit < 0 ? 'text-red-700' : ''}`}>{money(Math.round(r.profit))}</td>
                  <td className={`px-2 py-1 text-right ${r.profit < 0 ? 'text-red-700' : ''}`}>{pct(r.rev ? r.profit / r.rev : null)}</td>
                  <td className={`px-2 py-1 text-right ${d === null ? 'text-ink-faint' : d < 0 ? 'text-red-700' : 'text-green-700'}`}>{d === null ? '—' : signed(Math.round(d))}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.manager || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/**
 * 거래처 묶음 고치기(10-28 way) — 원이름 하나씩 이 묶음에 넣거나 뺀다(서버 `saveProfitCustGroup` · 화면 권한 profit 입력).
 * 다른 묶음에 있던 이름을 넣으면 그쪽에서 옮겨 온다. 바꾼 즉시 거래처별·추세·노선 탭과 수익 보고서가 새 묶음으로 읽는다.
 */
export function GroupEditor({ cust, members, onClose, onSaved }: { cust: string; members?: string[]; onClose: () => void; onSaved: (group: string) => void }) {
  const [names, setNames] = useState<{ raw_name: string; grp: string | null; last_mon: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const [list, setList] = useState(members ?? [cust]);
  // 거래처별 탭(#422)은 묶음 구성원을 모른다 → 이름 목록에서 이 묶음에 든 원이름을 찾는다.
  useEffect(() => {
    api.listProfitCustNames({}).then((r) => {
      setNames(r.rows);
      if (!members) {
        const inGroup = r.rows.filter((n) => (n.grp ?? n.raw_name) === cust).map((n) => n.raw_name);
        setList([cust, ...inGroup.filter((x) => x !== cust)]);
      }
    }).catch((e) => setError(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const groupOf = useMemo(() => new Map((names ?? []).map((n) => [n.raw_name, n.grp ?? n.raw_name])), [names]);
  const save = async (rawName: string, group: string, after: (l: string[]) => string[]) => {
    setBusy(true); setError(null);
    try { await api.saveProfitCustGroup({ rawName, group }); setList(after); setChanged(true); return true; }
    catch (e) { setError(errMsg(e)); return false; } finally { setBusy(false); }
  };
  const addName = add.trim();
  const addFrom = addName ? groupOf.get(addName) : undefined;
  return (
    <Modal width="max-w-xl">
      <div className="card space-y-3 p-4">
        <h3 className="text-base font-semibold break-keep">거래처 묶음 고치기 — {cust}</h3>
        <p className="text-xs text-ink-soft">계열사·캠퍼스·청구처처럼 이름만 갈라진 거래처를 한 줄로 봅니다. 고치면 거래처별·거래처 추세·노선별 탭과 수익 보고서가 바로 새 묶음으로 셉니다(굳힌 월 수익 기록은 그대로).</p>
        {error && <ErrorBox message={error} />}
        <ul className="divide-y divide-line rounded border border-line text-sm">
          {list.map((m) => (
            <li key={m} className="flex items-center gap-2 px-2 py-1.5">
              <span className="min-w-0 flex-1 break-keep">{m}</span>
              {m === cust
                ? <span className="text-xs text-ink-faint">묶음 이름</span>
                : <button className="btn px-2 py-0.5 text-xs" disabled={busy} onClick={() => void save(m, '', (l) => l.filter((x) => x !== m))}>묶음에서 빼기</button>}
            </li>
          ))}
        </ul>
        <div className="space-y-1">
          <div className="flex gap-2">
            <input className="field min-w-0 flex-1" list="profit-cust-names" placeholder={names ? '넣을 거래처 이름(버스인 이름 그대로)' : '이름 불러오는 중…'}
                   value={add} onChange={(e) => setAdd(e.target.value)} />
            <button className="btn-primary" disabled={busy || !addName || list.includes(addName) || !groupOf.has(addName)}
                    onClick={() => void save(addName, cust, (l) => [...l, addName]).then((ok) => { if (ok) setAdd(''); })}>넣기</button>
          </div>
          <datalist id="profit-cust-names">
            {(names ?? []).filter((n) => !list.includes(n.raw_name)).map((n) => (
              <option key={n.raw_name} value={n.raw_name}>{n.grp && n.grp !== n.raw_name ? `지금 ${n.grp} · ` : ''}마지막 {n.last_mon}</option>
            ))}
          </datalist>
          {addName && names && !groupOf.has(addName) && <div className="text-xs text-red-700">거래처 추세 기록에 없는 이름입니다 — 목록에서 고르세요.</div>}
          {addFrom && addFrom !== addName && addFrom !== cust && <div className="text-xs text-amber-700">지금 「{addFrom}」 묶음에 있습니다 — 넣으면 이 묶음으로 옮깁니다.</div>}
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={() => (changed ? onSaved(cust) : onClose())}>{changed ? '닫고 다시 보기' : '닫기'}</button>
        </div>
      </div>
    </Modal>
  );
}

function Stat({ label, value, tone, sub, subTone }: { label: string; value: string; tone?: boolean; sub?: string; subTone?: boolean }) {
  return (
    <div className="rounded border border-line px-2 py-1.5">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className={`font-semibold tabular-nums ${tone ? 'text-red-700' : ''}`}>{value}</div>
      {sub && <div className={`text-xs tabular-nums ${subTone ? 'text-red-700' : 'text-ink-soft'}`}>{sub}</div>}
    </div>
  );
}

/** 달별 매출·수익 두 선(한 축 · 원). 없는 달은 끊는다. 이번 달(진행 중)은 점선 끝. */
function TrendChart({ axis, byMon, cur }: { axis: string[]; byMon: Map<string, MonAgg>; cur: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720, H = 220, L = 56, R = 12, T = 12, B = 26;
  const series = [
    { key: 'rev' as const, label: '매출', cls: 'stroke-ink-soft', w: 1.8 },
    { key: 'profit' as const, label: '수익', cls: 'stroke-brand-600', w: 2.5 },
  ];
  const vals = axis.flatMap((m) => { const r = byMon.get(m); return r ? [r.rev, r.profit] : []; });
  if (!vals.length) return null;
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  const raw = (hi - lo || 1) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 5, 10].map((k) => k * pow).find((s) => s >= raw)!;
  const min = Math.floor(lo / unit) * unit, max = Math.ceil(hi / unit) * unit;
  const n = Math.max(axis.length - 1, 1);
  const x = (i: number) => L + (i * (W - L - R)) / n;
  const y = (v: number) => T + (1 - (v - min) / (max - min || 1)) * (H - T - B);
  const ticks: number[] = []; for (let t = min; t <= max + 1e-6; t += unit) ticks.push(t);
  const every = Math.ceil(axis.length / 12);
  // 마지막 달 이름은 바로 앞 눈금과 반 칸 넘게 떨어질 때만(붙으면 「26.08 26.09」처럼 겹친다)
  const lastTick = axis.length - 1 - ((axis.length - 1) % every) < axis.length - 1 - every / 2;
  const hm = hover !== null ? axis[hover] : undefined;
  const hr = hm ? byMon.get(hm) : undefined;
  return (
    <div className="relative">
      <div className="mb-1 flex gap-4 text-xs text-ink-soft">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1">
            <svg width="22" height="8"><line x1="0" x2="22" y1="4" y2="4" className={s.cls} strokeWidth={s.w} /></svg>{s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="달별 매출·수익" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} className={t === 0 ? 'stroke-ink-faint' : 'stroke-line'} strokeWidth={1} />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" className="fill-ink-soft text-[10px]">{eok(t)}</text>
          </g>
        ))}
        {axis.map((m, i) => (i % every === 0 || (i === axis.length - 1 && lastTick)) && (
          <text key={m} x={x(i)} y={H - 8} textAnchor="middle" className="fill-ink-soft text-[10px]">{m.slice(2).replace('-', '.')}</text>
        ))}
        {series.map((s) => {
          const segs: string[][] = []; let c: string[] = [];
          axis.forEach((m, i) => { const r = byMon.get(m); if (!r) { if (c.length) segs.push(c); c = []; } else c.push(`${x(i)},${y(r[s.key])}`); });
          if (c.length) segs.push(c);
          return segs.map((g, k) => g.length === 1
            ? <circle key={`${s.key}-${k}`} cx={g[0]!.split(',')[0]} cy={g[0]!.split(',')[1]} r={2.5} className={`fill-surface ${s.cls}`} strokeWidth={2} />
            : <polyline key={`${s.key}-${k}`} fill="none" className={s.cls} strokeWidth={s.w} strokeLinejoin="round" points={g.join(' ')} />);
        })}
        {axis.at(-1) === cur && byMon.has(cur) && (
          <text x={x(axis.length - 1)} y={T + 8} textAnchor="end" className="fill-amber-700 text-[10px]">진행 중</text>
        )}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="stroke-ink-faint" strokeDasharray="3 3" />}
        {axis.map((m, i) => (
          <rect key={m} x={x(i) - (W - L - R) / n / 2} y={T} width={(W - L - R) / n} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && hm && (
        <div className="pointer-events-none absolute top-6 rounded border border-line bg-surface px-2 py-1 text-xs shadow"
             style={{ left: `${Math.min(74, (x(hover) / W) * 100)}%` }}>
          <div className="font-semibold">{hm}{hm === cur ? ' (진행 중)' : ''}</div>
          {hr ? <>
            <div>매출 {money(Math.round(hr.rev))}</div>
            <div>수익 {money(Math.round(hr.profit))} · {pct(hr.rev ? hr.profit / hr.rev : null)}</div>
            {hr.manager && <div className="text-ink-soft">버스인접수담당자 {hr.manager}</div>}
          </> : <div className="text-ink-faint">기록 없음</div>}
        </div>
      )}
    </div>
  );
}
